import { useCallback } from 'react';

import {
   CurrentHealthcareProviderEwusConfigurationQuery,
   HealthcareProviderEwusConfiguration,
} from '@dreryk/egabinet-ewus-frontend-api';
import {
   CurrentPractitionerNfzConfigurationQuery,
   HealthcareProviderNfzConfiguration,
} from '@dreryk/egabinet-nfz-configuration-frontend-api';

import { useCqrsFind } from '@dreryk/command-query-react-common';
import { PromiseVoidFunction } from '@dreryk/react-common';
import { StandardSpinner, StyledMargin } from '@dreryk/react-components';

type EwusConfigurationStatus = 'CONFIGURED' | 'NOT_CONFIGURED' | 'FETCH_ERROR';

type EwusPatientDownloadLoaderChildrenRenderProps = {
   ewusConfigurationStatus: EwusConfigurationStatus;
   refetchConfiguration: PromiseVoidFunction;
};

type EwusPatientDownloadLoaderProps = {
   readonly children: (renderProps: EwusPatientDownloadLoaderChildrenRenderProps) => JSX.Element;
};

export const EwusPatientDownloadLoader = (props: EwusPatientDownloadLoaderProps): JSX.Element => {
   const {
      data: currentPratitionerNfzConfiguration,
      error: currentPratitionerNfzConfigurationError,
      fetching: currentPratitionerNfzConfigurationFetching,
      refetch: refetchCurrentPractitionerNfzConfiguration,
   } = useCqrsFind<HealthcareProviderNfzConfiguration>(() => {
      return new CurrentPractitionerNfzConfigurationQuery();
   });

   const {
      data: ewusConfiguration,
      fetching: ewusConfigurationFetching,
      error: ewusConfigurationError,
      refetch: refetchEwusConfiguration,
   } = useCqrsFind<HealthcareProviderEwusConfiguration>(() => {
      return new CurrentHealthcareProviderEwusConfigurationQuery();
   });

   const refetchConfiguration = useCallback(async () => {
      await Promise.all([refetchCurrentPractitionerNfzConfiguration(), refetchEwusConfiguration()]);
   }, [refetchCurrentPractitionerNfzConfiguration, refetchEwusConfiguration]);

   if (currentPratitionerNfzConfigurationFetching || ewusConfigurationFetching) {
      return (
         <StyledMargin top={39}>
            <StandardSpinner />
         </StyledMargin>
      );
   }

   if (currentPratitionerNfzConfigurationError || ewusConfigurationError) {
      return props.children({ ewusConfigurationStatus: 'FETCH_ERROR', refetchConfiguration: refetchConfiguration });
   }

   return props.children({
      ewusConfigurationStatus:
         currentPratitionerNfzConfiguration && ewusConfiguration ? 'CONFIGURED' : 'NOT_CONFIGURED',
      refetchConfiguration: refetchConfiguration,
   });
};
